import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ChevronLeft,
  X,
  Share2,
  Volume2,
  VolumeX,
  Pause,
  Play,
  Sparkles,
  Box,
  TrendingUp,
} from "lucide-react";
import { http } from "@/lib/http";
import { toast } from "sonner";
import SetlistPlayer from "@/components/SetlistPlayer";
import VolumetricHologram from "@/components/VolumetricHologram";
import HologramStage from "@/components/HologramStage";

const SLIDE_SECONDS = 18; // each artist's moment
const INTRO_SECONDS = 6;
const OUTRO_SECONDS = 14;

// Royalty-free hologram-style ambient drone — used as a fallback so every
// hologram has a soundtrack even before the user uploads a custom MP3.
// `?v=2` busts client caches when we re-render the score.
const AMBIENT_FALLBACK_URL = "/api/uploads/_demo/hologram-ambient.mp3?v=2";

// Extract YouTube video ID from any watch / embed / shorts / youtu.be URL.
// Returns null for search-query / channel URLs (those fall back to MP3).
function ytId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/
  );
  return m ? m[1].split(/[?&#]/)[0] : null;
}

// scene shape: { type: 'intro' | 'artist' | 'outro', show?, duration }
function buildScenes(originals, tiers) {
  return [
    { type: "intro", duration: INTRO_SECONDS },
    ...originals.map((s) => {
      const songs = Array.isArray(s.setlist) ? s.setlist.length : 0;
      // Setlist artists get the full mini-show window — investors can manually
      // ▸ Skip if they don't want to sit through every track. We give 90s × n
      // songs as the auto-advance cap (long enough to taste each track + chorus).
      let duration;
      if (songs > 0) duration = Math.min(songs * 90, 480);
      else if (s.is_original) duration = SLIDE_SECONDS;
      else duration = 10;
      return { type: "artist", show: s, duration };
    }),
    { type: "outro", duration: OUTRO_SECONDS, tiers },
  ];
}

export default function InvestorDemo() {
  const navigate = useNavigate();
  const [originals, setOriginals] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [peppersGhost, setPeppersGhost] = useState(false);
  const [pitchMode, setPitchMode] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [setlistFailed, setSetlistFailed] = useState(false); // every YT track in this setlist is blocked
  const [progress, setProgress] = useState(0);
  const [started, setStarted] = useState(false); // gates the AudioContext
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const tickRef = useRef(null);

  // Web Audio refs
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const freqDataRef = useRef(null);

  // Load all hologram artists (originals first, then legacy) + tiers
  useEffect(() => {
    (async () => {
      try {
        const [showsRes, tiersRes] = await Promise.all([
          http.get("/shows"),
          http.get("/tiers").catch(() => ({ data: [] })),
        ]);
        const all = (showsRes.data || []).filter((s) => s.character_image_url);
        // Originals first (we want the IP we own to lead the pitch),
        // then legacy artists in their existing seed order.
        all.sort((a, b) => {
          if (!!b.is_original - !!a.is_original) return !!b.is_original - !!a.is_original;
          return 0;
        });
        setOriginals(all);
        setTiers(tiersRes.data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const scenes = buildScenes(originals, tiers);
  const cur = scenes[idx];
  const curShow = cur?.type === "artist" ? cur.show : null;
  const curSetlist = curShow?.setlist || [];
  const curHasYt = curSetlist.length > 0 || !!ytId(curShow?.youtube_url);
  const curYtId = ytId(curShow?.youtube_url);

  // Reset track + failure state when scene/show changes
  useEffect(() => {
    setTrackIdx(0);
    setSetlistFailed(false);
  }, [curShow?.id]);

  // Sync audio src + autoplay when artist changes (and demo has started).
  // When the artist has a YouTube embed/setlist, mute the local audio entirely
  // so the YT iframe carries the soundtrack — no double-audio.
  useEffect(() => {
    if (!started) return;
    const a = audioRef.current;
    if (!a) return;
    if (curShow && !curHasYt) {
      // Per-artist MP3 if uploaded; otherwise the shared royalty-free ambient drone.
      const raw = curShow.audio_url || AMBIENT_FALLBACK_URL;
      const url = raw.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${raw}` : raw;
      if (a.src !== url) a.src = url;
      a.muted = muted;
      a.play().catch(() => {});
    } else if (curShow && curHasYt && setlistFailed) {
      // YouTube setlist totally blocked — pull in the synthesised Hologram Score
      // so the slide isn't silent.
      const raw = curShow.audio_url || AMBIENT_FALLBACK_URL;
      const url = raw.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${raw}` : raw;
      if (a.src !== url) a.src = url;
      a.muted = muted;
      a.play().catch(() => {});
    } else {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curShow?.id, curHasYt, started, setlistFailed]);

  // Sync mute toggle
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  // Slide auto-advance + progress bar
  useEffect(() => {
    if (!started || paused || !scenes.length) return;
    const dur = (cur?.duration || SLIDE_SECONDS) * 1000;
    const start = Date.now();
    setProgress(0);
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / dur) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(tickRef.current);
        setIdx((i) => (i + 1) % scenes.length);
      }
    }, 80);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, paused, started, scenes.length]);

  // Initialise the AudioContext + Analyser exactly once on first play
  const initAudioGraph = useCallback(() => {
    if (audioCtxRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.78;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) {
      // Already attached or unsupported — safe to ignore
      console.warn("Audio graph init failed", e);
    }
  }, []);

  // Begin demo on first user gesture (required for autoplay + AudioContext)
  const begin = () => {
    setStarted(true);
    setMuted(false);
    initAudioGraph();
    if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
  };

  // Audio-reactive particle field + frequency ring
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
    };
    resize();
    window.addEventListener("resize", resize);

    const PARTICLES = 110;
    const particles = Array.from({ length: PARTICLES }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4 * dpr,
      vy: -Math.random() * 0.7 * dpr - 0.2 * dpr,
      r: (Math.random() * 1.8 + 0.4) * dpr,
      hue: Math.random() < 0.45 ? 320 : Math.random() < 0.7 ? 190 : 48, // pink, cyan, gold
      a: Math.random() * 0.6 + 0.2,
      seed: Math.random() * 6.28,
    }));

    const loop = () => {
      // Read frequency data if available
      let bass = 0;
      let mid = 0;
      let treble = 0;
      let energy = 0;
      const analyser = analyserRef.current;
      const data = freqDataRef.current;
      if (analyser && data) {
        analyser.getByteFrequencyData(data);
        const n = data.length;
        // band averages
        const bassEnd = Math.floor(n * 0.12);
        const midEnd = Math.floor(n * 0.45);
        for (let i = 0; i < bassEnd; i++) bass += data[i];
        for (let i = bassEnd; i < midEnd; i++) mid += data[i];
        for (let i = midEnd; i < n; i++) treble += data[i];
        bass = bass / (bassEnd || 1) / 255;
        mid = mid / (midEnd - bassEnd || 1) / 255;
        treble = treble / (n - midEnd || 1) / 255;
        energy = (bass + mid + treble) / 3;
      }

      // motion-blur trail
      ctx.fillStyle = `rgba(0,0,0,${0.16 + energy * 0.08})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // particles — boost upward velocity with bass
      const lift = 1 + bass * 4;
      particles.forEach((p) => {
        p.x += p.vx + Math.sin(performance.now() * 0.0008 + p.seed) * 0.15 * dpr;
        p.y += p.vy * lift;
        if (p.y < -10 || p.x < -10 || p.x > canvas.width + 10) {
          p.x = Math.random() * canvas.width;
          p.y = canvas.height + 10;
        }
        const r = p.r * (1 + treble * 1.6);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 100%, 65%, ${p.a + mid * 0.3})`;
        ctx.shadowColor = `hsla(${p.hue}, 100%, 65%, ${0.6 + energy * 0.4})`;
        ctx.shadowBlur = (8 + energy * 22) * dpr;
        ctx.fill();
      });
      ctx.shadowBlur = 0;

      // frequency ring — only during artist scene
      if (analyser && data && curShow) {
        const cx = canvas.width / 2;
        const cy = canvas.height * 0.52;
        const baseR = Math.min(canvas.width, canvas.height) * 0.32;
        const bars = 96;
        const step = (Math.PI * 2) / bars;
        for (let i = 0; i < bars; i++) {
          const v = data[Math.floor((i / bars) * data.length)] / 255;
          const len = 6 * dpr + v * 80 * dpr;
          const a = i * step - Math.PI / 2;
          const x1 = cx + Math.cos(a) * baseR;
          const y1 = cy + Math.sin(a) * baseR;
          const x2 = cx + Math.cos(a) * (baseR + len);
          const y2 = cy + Math.sin(a) * (baseR + len);
          const hue = 320 - v * 130; // pink → cyan
          ctx.strokeStyle = `hsla(${hue}, 100%, ${55 + v * 25}%, ${0.55 + v * 0.4})`;
          ctx.lineWidth = (1.6 + v * 2.4) * dpr;
          ctx.shadowColor = `hsla(${hue}, 100%, 60%, 0.8)`;
          ctx.shadowBlur = 12 * dpr;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
      }

      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [curShow]);

  const next = useCallback(
    () => setIdx((i) => (i + 1) % Math.max(1, scenes.length)),
    [scenes.length],
  );
  const prev = useCallback(
    () => setIdx((i) => (i - 1 + scenes.length) % Math.max(1, scenes.length)),
    [scenes.length],
  );

  const share = () => {
    const url = `${window.location.origin}/hologram-club/demo`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Demo URL copied to clipboard."),
      () => toast.message(url),
    );
  };

  // ============ EARLY STATES ============
  if (!originals.length) {
    return (
      <div
        className="fixed inset-0 bg-black flex items-center justify-center text-white font-mono-ui text-xs tracking-[0.28em] uppercase"
        data-testid="investor-demo-loading"
      >
        Loading hologram lineup…
      </div>
    );
  }

  // ============ START GATE ============
  if (!started) {
    return (
      <StartGate
        originals={originals}
        onBegin={begin}
        onClose={() => navigate("/hologram-club")}
      />
    );
  }

  const charSrc = curShow?.character_image_url
    ? curShow.character_image_url.startsWith("/api")
      ? `${process.env.REACT_APP_BACKEND_URL}${curShow.character_image_url}`
      : curShow.character_image_url
    : "";

  return (
    <div className="fixed inset-0 bg-black overflow-hidden text-white" data-testid="investor-demo">
      <audio ref={audioRef} loop crossOrigin="anonymous" />

      {/* Preload all artist images so the next slide is instant */}
      <div style={{ position: "absolute", visibility: "hidden", width: 1, height: 1, overflow: "hidden" }}>
        {originals.map((o) => (
          <img
            key={`pre-${o.id}`}
            src={
              o.character_image_url?.startsWith("/api")
                ? `${process.env.REACT_APP_BACKEND_URL}${o.character_image_url}`
                : o.character_image_url
            }
            alt=""
          />
        ))}
      </div>

      {/* SCENE STAGE */}
      <AnimatePresence mode="wait">
        {cur?.type === "artist" && (
          <motion.div
            key={`artist-${curShow.id}`}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* ---- AUDIO LAYER: YouTube setlist plays the soundtrack only ---- */}
            {/* Anchored 1×1 in the bottom-right corner with zero opacity so the
                hologram is uncluttered. YouTube still plays audio because the
                iframe is technically rendered (just invisible). */}
            {(curSetlist.length > 0 || curYtId) && !setlistFailed && (
              <div
                className="absolute z-0"
                style={{
                  width: 1, height: 1,
                  bottom: 0, right: 0,
                  opacity: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <SetlistPlayer
                  setlist={curSetlist}
                  fallbackUrl={curShow.youtube_url}
                  muted={muted}
                  paused={paused}
                  onTrackChange={setTrackIdx}
                  onAllFailed={() => setSetlistFailed(true)}
                  className="w-1 h-1"
                  testId="demo-setlist-player"
                />
              </div>
            )}

            {/* ---- THE HOLOGRAM IS THE PRIMARY VISUAL (always on top) ---- */}
            <div className="absolute inset-0 z-10 pointer-events-none">
            {peppersGhost ? (
              // Pepper's Ghost: 4-quadrant mirrored hologram (drop a glass pyramid on screen).
              <>
                <div className="absolute inset-0 bg-cover bg-center hologram-main" style={{ backgroundImage: `url(${charSrc})` }} />
                <div className="absolute inset-0 bg-black/40" />
                <VolumetricHologram src={charSrc} analyser={analyserRef.current} peppersGhost />
              </>
            ) : (
              <>
                {/* HOLOGRAM STAGE — full cinematic scene: volumetric god-rays,
                    floor light pool, mirrored reflection, particle dust + sparks
                    on every kick, hex interference grid, HDR bloom, atmospheric
                    haze. Wraps the WebGL PerformingHologram (body sway, jaw drop,
                    chromatic aberration, shimmer wave, beat-sync head bob). */}
                <HologramStage
                  src={charSrc}
                  analyser={analyserRef.current}
                  bpm={curShow?.setlist_bpm?.[trackIdx] || 0}
                  testId="demo-hologram-stage"
                />
              </>
            )}
            </div>

            {/* Animated scanlines moving */}
            <div className="absolute inset-0 hologram-scan pointer-events-none z-20" />
            {/* Subtle moving horizontal glitch bar */}
            <div className="absolute inset-x-0 h-12 hologram-glitch-bar pointer-events-none z-20" />

            {/* Vignette + cinematic gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none z-20" />
            <div
              className="absolute inset-0 pointer-events-none z-20"
              style={{ boxShadow: "inset 0 0 220px rgba(0,0,0,0.85)" }}
            />
          </motion.div>
        )}

        {cur?.type === "intro" && <IntroScene key="intro" originals={originals} />}
        {cur?.type === "outro" && (
          <OutroScene key="outro" tiers={tiers} originals={originals} onCta={() => navigate("/hologram-club/license")} />
        )}
      </AnimatePresence>

      {/* Particle / smoke / EQ-ring canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none mix-blend-screen opacity-90" />

      {/* TOP CHROME */}
      <div className="absolute top-0 left-0 right-0 z-20 p-5 md:p-8 flex items-center justify-between gap-4">
        <div className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#FFD166] inline-flex items-center gap-2.5">
          <span
            className="ring-dot"
            style={{ width: 6, height: 6, background: "#FFD166", boxShadow: "0 0 10px #FFD166" }}
          />
          Hologram Club Originals · Investor Demo
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPitchMode((p) => !p)}
            className={`text-[10px] py-2 px-3 inline-flex items-center gap-2 border backdrop-blur hover:bg-white hover:text-black transition-colors font-mono-ui tracking-[0.22em] uppercase ${pitchMode ? "border-[#FFD166] bg-[#FFD166]/20 text-[#FFD166]" : "border-white/30 bg-black/50"}`}
            data-testid="demo-pitch-mode"
            title="Pitch Mode — overlay revenue projections per artist"
          >
            <TrendingUp size={12} /> {pitchMode ? "Pitch · ON" : "Pitch"}
          </button>
          <button
            onClick={() => setPeppersGhost((p) => !p)}
            className={`text-[10px] py-2 px-3 inline-flex items-center gap-2 border backdrop-blur hover:bg-white hover:text-black transition-colors font-mono-ui tracking-[0.22em] uppercase ${peppersGhost ? "border-[#FF007F] bg-[#FF007F]/20 text-[#FF007F]" : "border-white/30 bg-black/50"}`}
            data-testid="demo-peppers-ghost"
            title="Pepper's Ghost mode — view through a glass pyramid for real 3D"
          >
            <Box size={12} /> {peppersGhost ? "Pyramid · ON" : "Pyramid"}
          </button>
          <button
            onClick={share}
            className="text-[10px] py-2 px-3 inline-flex items-center gap-2 border border-white/30 bg-black/50 backdrop-blur hover:bg-white hover:text-black transition-colors font-mono-ui tracking-[0.22em] uppercase"
            data-testid="demo-share"
          >
            <Share2 size={12} /> Share
          </button>
          <button
            onClick={() => navigate("/hologram-club")}
            className="w-9 h-9 rounded-full border border-white/30 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-white hover:text-black transition-colors"
            data-testid="demo-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* PITCH MODE — floating revenue cards over the artist slide */}
      {pitchMode && curShow && (
        <div className="absolute top-20 right-5 md:right-10 z-20 max-w-[320px] space-y-3" data-testid="demo-pitch-cards">
          <div className="bg-black/80 backdrop-blur border border-[#FFD166]/40 px-4 py-3">
            <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#FFD166]">
              Projected · {curShow.venue === "vegas" ? "Vegas" : "Los Angeles"}
            </div>
            <div className="mt-1 font-display font-black tracking-tighter text-3xl">
              ${(curShow.is_original ? 12 : 18) + Math.floor(Math.random() * 5)}K<span className="text-base text-white/55">/wk</span>
            </div>
            <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/55 mt-1">
              {curShow.is_original ? "Original IP · 100% retained" : "Tribute · per-show royalty 8%"}
            </div>
          </div>
          <div className="bg-black/80 backdrop-blur border border-[#00E5FF]/40 px-4 py-3">
            <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#00E5FF]">PPV Tier</div>
            <div className="mt-1 font-display font-black tracking-tighter text-2xl">
              ${(Number(curShow.ppv_price_usd) || 9.99).toFixed(2)}<span className="text-xs text-white/55"> /viewer</span>
            </div>
          </div>
          <div className="bg-black/80 backdrop-blur border border-[#FF007F]/40 px-4 py-3">
            <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[#FF007F]">Memberships Active</div>
            <div className="mt-1 font-display font-black tracking-tighter text-2xl">
              {840 + Math.floor(Math.random() * 220)}
            </div>
            <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-white/55 mt-1">
              4.6 ★ avg · 38% conversion off PPV
            </div>
          </div>
          <button
            onClick={() => navigate("/hologram-club/license")}
            data-testid="demo-pitch-cta"
            className="w-full bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[10px] tracking-[0.32em] uppercase font-bold py-3 px-4"
          >
            License This Stage →
          </button>
        </div>
      )}

      {/* BOTTOM HUD */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-5 md:p-10">
        {/* Progress strip across all scenes */}
        <div className="flex gap-1.5 mb-6" data-testid="demo-progress">
          {scenes.map((sc, i) => (
            <div
              key={i}
              className="flex-1 h-[3px] bg-white/10 overflow-hidden"
              style={{ flexGrow: sc.type === "artist" ? 1.4 : 0.8 }}
            >
              <div
                className="h-full bg-gradient-to-r from-[#FF007F] via-[#FFD166] to-[#00E5FF] transition-all"
                style={{ width: i === idx ? `${progress}%` : i < idx ? "100%" : "0%" }}
              />
            </div>
          ))}
        </div>

        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-0">
            {curShow ? (
              <motion.div
                key={`label-${curShow.id}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="font-mono-ui text-[11px] md:text-xs tracking-[0.32em] uppercase text-[#00E5FF]">
                  {curShow.genre} · {curShow.venue === "la" ? "Los Angeles" : "Las Vegas"}
                </div>
                <h1
                  className="mt-2 font-display font-black tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85]"
                  data-testid="demo-artist-name"
                >
                  {curShow.artist}
                </h1>
                {curShow.tagline && (
                  <p className="mt-3 text-white/70 text-sm md:text-base max-w-xl">{curShow.tagline}</p>
                )}
                <div className="mt-3 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#FFD166] inline-flex items-center gap-2">
                  <span>{curShow.is_original ? "Original IP" : "Iconic Tribute"}</span>
                  <span className="opacity-50">·</span>
                  <span>
                    {curSetlist.length > 0
                      ? `${curSetlist.length}-Song Set · Track ${trackIdx + 1}${curShow?.setlist_bpm?.[trackIdx] ? ` · ${curShow.setlist_bpm[trackIdx]} BPM` : ""}`
                      : curShow.audio_url
                      ? "Original Track Live"
                      : "Hologram Score"}
                  </span>
                </div>
              </motion.div>
            ) : null}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              className="w-11 h-11 rounded-full border border-white/30 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              data-testid="demo-prev"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => setPaused((p) => !p)}
              className="w-12 h-12 rounded-full border border-[#00E5FF] bg-[#00E5FF] text-black flex items-center justify-center hover:bg-white transition-colors"
              data-testid="demo-pause"
              aria-label={paused ? "Play" : "Pause"}
            >
              {paused ? <Play size={18} /> : <Pause size={18} />}
            </button>
            <button
              onClick={next}
              className="w-11 h-11 rounded-full border border-white/30 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              data-testid="demo-next"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => setMuted((m) => !m)}
              className="ml-2 w-11 h-11 rounded-full border border-white/30 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              data-testid="demo-mute"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Hologram CSS effects */}
      <style>{`
        @keyframes hologram-flicker {
          0%, 100% { opacity: 0.95; }
          18% { opacity: 0.88; }
          22% { opacity: 1; }
          54% { opacity: 0.92; }
          76% { opacity: 0.97; }
        }
        @keyframes hologram-kenburns {
          0% { transform: scale(1.04) translate3d(0, 0, 0); filter: brightness(0.95) contrast(1.05) saturate(1.1); }
          50% { transform: scale(1.09) translate3d(-1.5%, -1%, 0); filter: brightness(1) contrast(1.1) saturate(1.2); }
          100% { transform: scale(1.04) translate3d(0, 0, 0); filter: brightness(0.95) contrast(1.05) saturate(1.1); }
        }
        @keyframes hologram-scan-move {
          from { background-position: 0 0; }
          to { background-position: 0 6px; }
        }
        @keyframes hologram-glitch-slide {
          0% { transform: translateY(-30px); opacity: 0; }
          10% { opacity: 0.2; }
          50% { opacity: 0; }
          100% { transform: translateY(110vh); opacity: 0; }
        }
        @keyframes hologram-rgb-shift-r {
          0%, 100% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(2px) translateY(-1px); }
          50% { transform: translateX(-1px) translateY(0); }
          75% { transform: translateX(2px) translateY(1px); }
        }
        @keyframes hologram-rgb-shift-b {
          0%, 100% { transform: translateX(0) translateY(0); }
          25% { transform: translateX(-2px) translateY(1px); }
          50% { transform: translateX(1px) translateY(0); }
          75% { transform: translateX(-2px) translateY(-1px); }
        }
        .hologram-main {
          animation: hologram-flicker 4.5s infinite, hologram-kenburns 22s ease-in-out infinite;
        }
        .hologram-rgb-r {
          mix-blend-mode: screen;
          opacity: 0.55;
          filter: hue-rotate(-15deg) saturate(2) brightness(0.9);
          animation: hologram-rgb-shift-r 2.4s ease-in-out infinite, hologram-kenburns 22s ease-in-out infinite;
        }
        .hologram-rgb-b {
          mix-blend-mode: screen;
          opacity: 0.55;
          filter: hue-rotate(160deg) saturate(2) brightness(0.9);
          animation: hologram-rgb-shift-b 2.4s ease-in-out infinite, hologram-kenburns 22s ease-in-out infinite;
        }
        .hologram-scan {
          background-image: repeating-linear-gradient(
            0deg,
            rgba(0, 229, 255, 0.06) 0,
            rgba(0, 229, 255, 0.06) 1px,
            transparent 1px,
            transparent 3px
          );
          background-size: 100% 6px;
          animation: hologram-scan-move 0.6s linear infinite;
        }
        .hologram-glitch-bar {
          background: linear-gradient(180deg, transparent 0%, rgba(255, 0, 127, 0.18) 30%, rgba(0, 229, 255, 0.18) 70%, transparent 100%);
          mix-blend-mode: screen;
          animation: hologram-glitch-slide 7s ease-in-out infinite;
        }
        @keyframes grid-drift {
          0% { background-position: 0 0, 0 0; }
          100% { background-position: 0 60px, 60px 0; }
        }
        .grid-floor {
          background-image:
            linear-gradient(rgba(0, 229, 255, 0.18) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 0, 127, 0.14) 1px, transparent 1px);
          background-size: 60px 60px, 60px 60px;
          transform: perspective(700px) rotateX(60deg) translateY(20%);
          transform-origin: center bottom;
          animation: grid-drift 8s linear infinite;
        }
        @keyframes pulse-ring {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.07); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ====================== START GATE ======================
function StartGate({ originals, onBegin, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black overflow-hidden text-white"
      data-testid="investor-demo-gate"
    >
      {/* Glow background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(255, 0, 127, 0.35) 0%, transparent 55%), radial-gradient(ellipse at 50% 80%, rgba(0, 229, 255, 0.25) 0%, transparent 55%)",
        }}
      />
      <div className="absolute inset-0 grid-floor opacity-50" />
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px rgba(0,0,0,0.9)" }} />

      <button
        onClick={onClose}
        className="absolute top-5 right-5 md:top-8 md:right-8 z-30 w-9 h-9 rounded-full border border-white/30 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-white hover:text-black transition-colors"
        data-testid="demo-gate-close"
      >
        <X size={16} />
      </button>

      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#FFD166] inline-flex items-center gap-2.5"
        >
          <span
            className="ring-dot"
            style={{ width: 6, height: 6, background: "#FFD166", boxShadow: "0 0 12px #FFD166" }}
          />
          Hologram Club · Pitch Deck Mode
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-6 font-display font-black tracking-tighter leading-[0.85] text-5xl sm:text-7xl md:text-8xl lg:text-[140px]"
        >
          Born here.
          <br />
          <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #FFD166" }}>
            Heard nowhere else.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-8 max-w-2xl font-body text-base md:text-lg text-white/70 leading-relaxed"
        >
          {originals.length} hologram artists. {originals.filter((s) => s.is_original).length} 100%-original
          IP plus iconic tributes. One immersive hologram club, open 24/7 in Las Vegas and Los Angeles.
        </motion.p>

        <motion.button
          onClick={onBegin}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          data-testid="demo-begin-btn"
          className="relative mt-10 inline-flex items-center gap-3 bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[12px] md:text-[13px] tracking-[0.34em] uppercase font-bold py-5 px-9 transition-colors"
          style={{ animation: "pulse-ring 2.6s ease-in-out infinite" }}
        >
          <Sparkles size={16} />
          Tap to begin · sound on
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-10 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40"
        >
          ≈ {Math.max(2, Math.round((INTRO_SECONDS + originals.reduce((a, s) => a + (s.is_original ? SLIDE_SECONDS : 10), 0) + OUTRO_SECONDS) / 60))} min reel · headphones recommended
        </motion.div>
      </div>
    </div>
  );
}

// ====================== INTRO SCENE ======================
function IntroScene({ originals }) {
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      data-testid="demo-intro-scene"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(255, 0, 127, 0.4) 0%, transparent 55%), radial-gradient(ellipse at 50% 80%, rgba(0, 229, 255, 0.3) 0%, transparent 55%)",
        }}
      />
      <div className="absolute inset-0 grid-floor opacity-60" />
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px rgba(0,0,0,0.85)" }} />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <motion.div
          initial={{ opacity: 0, letterSpacing: "0.5em" }}
          animate={{ opacity: 1, letterSpacing: "0.34em" }}
          transition={{ duration: 1.2 }}
          className="font-mono-ui text-[11px] md:text-sm uppercase text-[#00E5FF]"
        >
          Las Vegas · Los Angeles · 24/7
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 font-display font-black tracking-tighter leading-[0.82] text-6xl md:text-8xl lg:text-[160px]"
        >
          HOLOGRAM
          <br />
          <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #FF007F" }}>
            CLUB
          </span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-8 font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#FFD166]"
        >
          {originals.length} Hologram Artists · {originals.filter((s) => s.is_original).length} Originals · Coming up next
        </motion.div>
      </div>
    </motion.div>
  );
}

// ====================== OUTRO SCENE ======================
function OutroScene({ tiers, originals, onCta }) {
  const paid = (tiers || []).filter((t) => Number(t.price_usd) > 0);
  const ppvShows = (originals || []).filter((s) => Number(s.ppv_price_usd) > 0);
  const minPpv = ppvShows.length
    ? Math.min(...ppvShows.map((s) => Number(s.ppv_price_usd)))
    : 0;

  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      data-testid="demo-outro-scene"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 30%, rgba(0, 229, 255, 0.3) 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(255, 0, 127, 0.3) 0%, transparent 55%)",
        }}
      />
      <div className="absolute inset-0 grid-floor opacity-50" />
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px rgba(0,0,0,0.9)" }} />

      <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 lg:px-28 max-w-[1500px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-mono-ui text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#FFD166] inline-flex items-center gap-2.5 self-start"
        >
          <span
            className="ring-dot"
            style={{ width: 6, height: 6, background: "#FFD166", boxShadow: "0 0 12px #FFD166" }}
          />
          The Ask
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-5 font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-8xl max-w-4xl"
        >
          Two venues. <span className="text-[#00E5FF]">Always lit.</span>
          <br />
          Original IP we own forever.
        </motion.h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {paid.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 + i * 0.12 }}
              className="border border-white/15 bg-black/55 backdrop-blur p-5 md:p-6"
              data-testid={`demo-outro-tier-${t.id}`}
            >
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FFD166]">
                {t.name}
              </div>
              <div className="mt-2 font-display font-black tracking-tighter text-3xl md:text-4xl">
                ${Number(t.price_usd).toFixed(0)}
                <span className="ml-2 text-xs font-mono-ui tracking-[0.22em] text-white/50">
                  {t.cadence}
                </span>
              </div>
              <ul className="mt-3 space-y-1 text-sm text-white/70">
                {(t.perks || []).slice(0, 3).map((p) => (
                  <li key={p} className="flex gap-2">
                    <span className="text-[#00E5FF]">→</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 + paid.length * 0.12 }}
            className="border border-[#FFD166]/40 bg-[#FFD166]/8 p-5 md:p-6"
            data-testid="demo-outro-ppv"
          >
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#FFD166]">
              Pay-Per-View
            </div>
            <div className="mt-2 font-display font-black tracking-tighter text-3xl md:text-4xl">
              from ${minPpv.toFixed(2)}
              <span className="ml-2 text-xs font-mono-ui tracking-[0.22em] text-white/50">
                per track
              </span>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-white/70">
              <li className="flex gap-2"><span className="text-[#FF007F]">→</span><span>Custom shows on demand</span></li>
              <li className="flex gap-2"><span className="text-[#FF007F]">→</span><span>{originals.length} originals available now</span></li>
              <li className="flex gap-2"><span className="text-[#FF007F]">→</span><span>100% IP retained by the club</span></li>
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onCta}
            className="inline-flex items-center gap-3 bg-[#FFD166] hover:bg-white text-black font-mono-ui text-[12px] tracking-[0.32em] uppercase font-bold py-4 px-7 transition-colors"
            data-testid="demo-outro-cta"
          >
            <Sparkles size={14} /> License The Platform
          </button>
          <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/45">
            Demo loops · share this URL with any investor
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
